<script setup lang="ts">
const emit = defineEmits(["close"]);
const props = defineProps({
  collectible: {
    type: Object as PropType<Collectible>,
    required: true,
  },
});

const { makeOfferOnCollectible, signOfferOnCollectible } =
  useCollectiblesController();
const { refreshUserFinancials } = useBalanceRefresh();
const { getUserBalance } = useUserController();
const { trackEvent } = useGAnalytics();
const userStore = useUserStore();
const isModalOfferLoginShown = ref<boolean>(false);
const isModalOfferMakeBeginShown = ref<boolean>(false);
const isModalOfferConfirmShown = ref<boolean>(false);
const isModal2FAShown = ref<boolean>(false);
const isModalOfferMakeEndShown = ref<boolean>(false);
const success = ref<boolean>(false);
const isProcessing = ref<boolean>(false);
const KYC_URI = ref<string>("");
const offerAmount = ref<string>("");
const submitOfferResponse = ref<Offer>({} as Offer);
const errorMessage = ref<string>("");

const setOffer = async () => {
  isModalOfferMakeBeginShown.value = false;
  isModalOfferConfirmShown.value = true;
};

const canSubmitOffer = () => {
  return userStore.transactionCount > 0;
};

const submitOffer = async () => {
  trackEvent("make_offer_confirm");
  try {
    const res = await makeOfferOnCollectible(
      props.collectible.id,
      offerAmount.value
    );
    if (!res || !res.payload) {
      throw new Error("Failed to make offer");
    }
    if (res.statusCode === 200) {
      submitOfferResponse.value = res.payload;
      signSubmitOffer();
    } else {
      handleError();
    }
  } catch (error: any) {
    if (error.statusCode === 418) {
      handleKYCRequired(error.data.payload.verificationURI);
    } else {
      handleErrorMessage(error);
      handleError();
    }
    trackEvent("make_offer_error");
  }
};

const signSubmitOffer = async (code: string = "") => {
  try {
    const res = await signOfferOnCollectible(
      submitOfferResponse.value.collectibleId,
      submitOfferResponse.value.id,
      submitOfferResponse.value.operationHash,
      code
    );
    if (!res) {
      throw new Error("Failed to sign offer");
    }
    if (res.statusCode === 200) {
      success.value = true;
      refreshUserFinancials();
      isModal2FAShown.value = false;
      isModalOfferMakeEndShown.value = true;
      trackEvent("make_offer_success");
    } else if (res.statusCode === 202) {
      isProcessing.value = true;
      isModal2FAShown.value = false;
      isModalOfferMakeEndShown.value = true;
    } else {
      handleError();
    }
  } catch (error: any) {
    trackEvent("make_offer_error");
    if (error.statusCode === 400) {
      isModal2FAShown.value = true;
      isModalOfferConfirmShown.value = false;
      return;
    } else if (error.statusCode === 418) {
      handleKYCRequired(error.data.payload.verificationURI);
    } else {
      handleErrorMessage(error);
      handleError();
    }
  } finally {
    isModalOfferConfirmShown.value = false;
  }
};

const handleKYCRequired = (URI: string) => {
  KYC_URI.value = URI;
  isModalOfferMakeBeginShown.value = false;
  isModalOfferConfirmShown.value = true;
};

const handleErrorMessage = (error: any) => {
  switch (error.statusCode) {
    case 402:
      errorMessage.value = "errorMessages.userWalletInsufficientFundsTransfer";
      break;
    case 403:
      errorMessage.value = "errorMessages.userWalletNotCreated";
      break;
    case 406:
      errorMessage.value = "errorMessages.offerInvalidMinimumPrice";
      break;
    case 412:
      errorMessage.value = "errorMessage.offerBlocked";
      break;
    case 418:
      handleKYCRequired(error.data.payload.verificationURI);
      break;
    case 433:
      errorMessage.value = "errorMessages.offerAcceptedMismatch";
      break;
    case 451:
      errorMessage.value = "errorMessages.userKYCBanned";
      break;
    default:
      errorMessage.value = "errorMessages.default";
      break;
  }
};

const handleError = () => {
  isModalOfferMakeBeginShown.value = false;
  isModalOfferConfirmShown.value = false;
  isModal2FAShown.value = false;
  isModalOfferMakeEndShown.value = true;
};

const handleBack = () => {
  isModalOfferConfirmShown.value = false;
  isModalOfferMakeBeginShown.value = true;
};

const closeAll = () => {
  emit("close");
};

watch(
  () => userStore.authToken,
  (newVal) => {
    if (newVal) {
      isModalOfferLoginShown.value = false;
      getUserBalance();
      isModalOfferMakeBeginShown.value = true;
    }
  }
);

onMounted(async () => {
  if (!userStore.authToken) {
    isModalOfferLoginShown.value = true;
  } else {
    getUserBalance();
    isModalOfferMakeBeginShown.value = true;
  }
});
</script>
<template>
  <BaseLayoutModal
    width="medium"
    @close="$emit('close')"
    @mouse-up="$emit('close')">
    <template #header>
      {{
        isModalOfferLoginShown
          ? $t("main.modalOfferLogin.loginOrSignUp")
          : $t("main.modalOfferBegin.title")
      }}
    </template>
    <template #content>
      <Transition mode="out-in">
        <ModalOfferMakeLogin v-if="isModalOfferLoginShown" @close="closeAll" />
        <ModalOfferMakeBegin
          v-else-if="isModalOfferMakeBeginShown"
          v-model:offerAmount="offerAmount"
          :collectible="collectible"
          @offer-set="setOffer"
          @close="closeAll" />
        <ModalOfferMakeConfirm
          v-else-if="isModalOfferConfirmShown"
          :offer-amount="offerAmount"
          :kyc-uri="KYC_URI"
          @offer-confirmed="submitOffer"
          @error="handleError"
          @back="handleBack"
          @close="closeAll" />
        <Modal2FA
          v-else-if="isModal2FAShown"
          @close2-f-a="$emit('close')"
          @2-f-a-submitted="signSubmitOffer" />
        <ModalOfferMakeEnd
          v-else-if="isModalOfferMakeEndShown"
          :success="success"
          :error-message="errorMessage"
          :amount="offerAmount"
          :is-processing="isProcessing"
          @close="closeAll" />
      </Transition>
    </template>
  </BaseLayoutModal>
</template>

<style scoped>
.v-enter-active {
  transition: all 0.3s ease-out;
}
.v-enter-from {
  opacity: 0;
  transform: translate(0, 1rem);
}
.v-enter-to {
  opacity: 1;
  transform: translate(0, 0);
}
.v-leave-active {
  transition: all 0.2s ease-in;
}
.v-leave-from {
  opacity: 1;
  transform: translate(0, 0);
}
.v-leave-to {
  opacity: 0;
  transform: translate(0, 1rem);
}
@media (min-width: 640px) {
  .v-enter-from,
  .v-leave-to {
    transform: translate(0, 0) scale(0.95);
  }
  .v-enter-to,
  .v-leave-from {
    transform: translate(0, 0) scale(1);
  }
}
</style>
